import { Injectable, Inject, EventEmitter } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface ScrollOptions {
  scrollTarget: string;
  scrollOffset?: number;
  duration?: number;
  document?: Document; // optional custom document
  scrollFinishListener?: EventEmitter<boolean>; // emitter on finish
}

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Smoothly scrolls to a target element
   */
  scroll(options: ScrollOptions): void {
    const activeDocument = options.document || this.document;
    const targetElement = activeDocument.querySelector(options.scrollTarget) as HTMLElement;

    if (!targetElement) {
      console.warn(`ScrollService: element not found for selector "${options.scrollTarget}"`);
      return;
    }

    const startPosition = window.scrollY || activeDocument.documentElement.scrollTop;
    const targetPosition = targetElement.getBoundingClientRect().top + startPosition;
    const scrollOffset = options.scrollOffset ?? 0;
    const duration = options.duration ?? 500;
    const distance = targetPosition - startPosition - scrollOffset;

    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-in-out cubic easing
      const easeInOutCubic = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const newPosition = startPosition + distance * easeInOutCubic;
      window.scrollTo(0, newPosition);

      if (elapsed < duration) {
        requestAnimationFrame(animateScroll);
      } else {
        // Fire callback on finish
        if (options.scrollFinishListener) {
          options.scrollFinishListener.emit(true);
        }
      }
    };

    requestAnimationFrame(animateScroll);
  }
}